import { useQuery } from '@tanstack/react-query';
import { graphQlClient } from '@config/graphqlClient';
import IncidentsGraphQL from '@graphql/incident.queries';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';

export const PENDING_INCIDENTS_QUERY_KEY = 'pending-incidents';

// Sprint 43, story-223. `enabled` (default true) lets the CLIENT home mount this only when the
// checklist's `activity` and `incidents` keys are both ON; every other caller is unchanged.
export const usePendingIncidents = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { organizationId } = useAppSelector(
    (state: RootState) => state.organization,
  );
  const userRole = useAppSelector((state: RootState) => state.user.role);
  const isSuperAdmin = userRole === UserRoles.SUPER_ADMIN;

  return useQuery({
    queryKey: [PENDING_INCIDENTS_QUERY_KEY, organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      const data: any = await graphQlClient.request(
        IncidentsGraphQL.queries.getPendingIncidents,
        { organizationId },
      );
      return data.getPendingIncidents || [];
    },
    // Disable for SUPER_ADMIN as they don't have access to this endpoint
    enabled: enabled && !!organizationId && !isSuperAdmin,
    staleTime: 0, // Always consider data stale for immediate updates
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    // No refetchInterval. useOrganizationEventsSubscription invalidates this
    // query the moment an incident actually changes, which is both fresher than
    // a 3s poll and vastly cheaper. The refetchOn* options below stay as the
    // fallback for a dropped socket.
    refetchOnMount: 'always', // Always refetch on component mount
    refetchOnWindowFocus: true, // Refetch when user returns to window
    refetchOnReconnect: true, // Refetch when network reconnects
  });
};
